<template>
  <div
    class="overview-commitment-card p-4 flex flex-col gap-3"
    :data-testid="`overview-commitment-card-${commitment.id}`"
  >
    <div class="flex items-start justify-between gap-3 flex-wrap">
      <div class="flex flex-col gap-2 min-w-0 flex-1 basis-[12rem]">
        <span
          class="overview-commitment-card__badge inline-block self-start"
          :class="badgeClass"
        >
          {{ badgeLabel }}
        </span>
        <p class="text-sm text-[var(--color-dark)] m-0">{{ commitment.was }}</p>
        <p class="text-[13px] text-[var(--color-disabled)] m-0">
          <template v-if="commitment.milestoneTitle">
            {{ $t('UserProjects.overview.myCommitments.milestoneLine', { name: commitment.milestoneTitle }) }}
            ·
          </template>
          {{ dueLine }}
        </p>
      </div>

      <div class="flex items-center gap-2 shrink-0 ml-auto">
        <span
          v-if="ownerInitials"
          class="overview-commitment-card__avatar"
          :title="commitment.ownerDisplayName || ''"
        >
          {{ ownerInitials }}
        </span>
        <div class="relative">
          <button
            type="button"
            class="overview-commitment-card__status-trigger"
            :data-testid="`overview-commitment-status-${commitment.id}`"
            @click="toggleDropdown"
          >
            {{ $t('UserProjects.overview.myCommitments.changeStatus') }}
            <i
              class="pi pi-chevron-down text-xs transition-transform"
              :class="{ 'rotate-180': showDropdown }"
              aria-hidden="true"
            />
          </button>
          <div
            v-if="showDropdown"
            class="overview-commitment-card__dropdown"
            data-testid="overview-commitment-status-menu"
          >
            <button
              v-for="option in statusOptions"
              :key="option.value"
              type="button"
              class="overview-commitment-card__dropdown-item"
              @click="selectStatus(option.value)"
            >
              <i v-if="option.icon" :class="option.icon" :style="{ color: option.dot }" />
              <span
                v-else
                class="overview-commitment-card__status-dot"
                :style="{ backgroundColor: option.dot }"
              />
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCauseForm"
      class="overview-commitment-card__cause border-t pt-3 flex flex-col gap-3"
      data-testid="overview-commitment-cause-form"
    >
      <label class="flex flex-col gap-1 text-sm">
        <span>{{ $t('ProjectManagement.commitments.cause.category') }}</span>
        <Select
          v-model="causeForm.category"
          :options="causeCategories"
          option-label="label"
          option-value="value"
          :placeholder="$t('ProjectManagement.commitments.cause.categoryPlaceholder')"
          class="w-full"
        />
      </label>
      <label class="flex flex-col gap-1 text-sm">
        <span>{{ $t('ProjectManagement.commitments.cause.freeText') }}</span>
        <Textarea v-model="causeForm.freeText" rows="2" class="w-full" />
      </label>
      <div class="flex items-center gap-2">
        <Checkbox v-model="causeForm.constraintRequested" binary input-id="overview-cause-constraint" />
        <label for="overview-cause-constraint" class="text-sm">
          {{ $t('ProjectManagement.commitments.cause.requestConstraint') }}
        </label>
      </div>
      <div class="flex gap-2 justify-end">
        <button type="button" class="overview-commitment-card__btn-secondary" @click="cancelCause">
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="overview-commitment-card__btn-primary btn-primary-accent"
          :disabled="saving"
          @click="submitCause"
        >
          {{ $t('ProjectManagement.save') }}
        </button>
      </div>
    </div>

    <CommitmentDoneDialog
      v-model:visible="doneDialogVisible"
      :commitment-label="commitment.was"
      :loading="saving"
      @submit="submitDone"
    />

    <div
      v-if="savedConfirmation"
      class="flex items-center gap-1.5 text-[13px] text-[#166534]"
    >
      <i class="pi pi-check text-sm" aria-hidden="true" />
      {{ savedConfirmation }}
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import CommitmentDoneDialog from '@/components/CustomerApp/UserProjects/CommitmentDoneDialog.vue';

const CAUSE_CATEGORIES = [
  'blocked',
  'resource',
  'dependency',
  'scope',
  'other',
];

export default {
  name: 'OverviewCommitmentCard',
  components: {
    Select,
    Textarea,
    Checkbox,
    CommitmentDoneDialog,
  },
  props: {
    commitment: {
      type: Object,
      required: true,
    },
  },
  emits: ['updated'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      showDropdown: false,
      showCauseForm: false,
      doneDialogVisible: false,
      saving: false,
      savedConfirmation: null,
      causeForm: {
        category: null,
        freeText: '',
        constraintRequested: false,
      },
    };
  },
  computed: {
    displayStatus() {
      return this.showCauseForm ? 'not_done' : this.commitment.status;
    },
    badgeLabel() {
      if (this.displayStatus === 'not_done') {
        return this.$t('UserProjects.overview.myCommitments.status.notDone');
      }
      if (this.displayStatus === 'done') {
        return this.$t('UserProjects.overview.myCommitments.status.done');
      }
      if (this.commitment.isOverdue) {
        return this.$t('UserProjects.overview.myCommitments.status.overdue');
      }
      return this.$t('UserProjects.overview.myCommitments.status.open');
    },
    badgeClass() {
      if (this.displayStatus === 'not_done') return 'overview-commitment-card__badge--not-done';
      if (this.displayStatus === 'done') return 'overview-commitment-card__badge--done';
      if (this.commitment.isOverdue) return 'overview-commitment-card__badge--overdue';
      return 'overview-commitment-card__badge--open';
    },
    ownerInitials() {
      const source = this.commitment.ownerDisplayName || '';
      if (!source) return '';
      const parts = source.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return source.slice(0, 2).toUpperCase();
    },
    dueLine() {
      if (!this.commitment.dueDate) {
        return this.$t('UserProjects.overview.myCommitments.noDueDate');
      }
      return this.$t('UserProjects.overview.myCommitments.dueLine', {
        date: new Date(this.commitment.dueDate).toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit',
        }),
      });
    },
    causeCategories() {
      return CAUSE_CATEGORIES.map((value) => ({
        value,
        label: this.$t(`ProjectManagement.commitments.cause.categories.${value}`),
      }));
    },
    statusOptions() {
      return [
        {
          value: 'done',
          label: this.$t('ProjectManagement.commitments.status.done'),
          dot: '#22C55E',
          icon: 'pi pi-check text-sm',
        },
        {
          value: 'not_done',
          label: this.$t('ProjectManagement.commitments.status.not_done'),
          dot: '#EF4444',
          icon: 'pi pi-times text-sm',
        },
      ];
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    ...mapActions('pmCommitment', ['updateCommitmentStatus', 'recordCause']),
    toggleDropdown(event) {
      event.stopPropagation();
      this.showDropdown = !this.showDropdown;
    },
    handleOutsideClick() {
      this.showDropdown = false;
    },
    selectStatus(status) {
      this.showDropdown = false;
      if (status === 'done') {
        this.doneDialogVisible = true;
        return;
      }
      if (status === 'not_done') {
        this.openCauseForm();
      }
    },
    async submitDone({ completionComment }) {
      this.saving = true;
      try {
        await this.updateCommitmentStatus({
          id: this.commitment.id,
          status: 'done',
          completionComment,
        });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.commitments.toast.statusUpdated'),
          life: 3000,
        });
        this.doneDialogVisible = false;
        this.$emit('updated');
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },
    openCauseForm() {
      this.causeForm = { category: null, freeText: '', constraintRequested: false };
      this.showCauseForm = true;
    },
    cancelCause() {
      this.showCauseForm = false;
    },
    async submitCause() {
      if (!this.causeForm.category) {
        this.toast.add({
          severity: 'warn',
          summary: this.$t('ProjectManagement.commitments.validation.causeCategoryRequired'),
          life: 4000,
        });
        return;
      }
      this.saving = true;
      try {
        await this.recordCause({
          id: this.commitment.id,
          payload: {
            category: this.causeForm.category,
            freeText: this.causeForm.freeText?.trim() || undefined,
            constraintRequested: this.causeForm.constraintRequested,
          },
        });
        await this.updateCommitmentStatus({ id: this.commitment.id, status: 'not_done' });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.commitments.toast.causeRecorded'),
          life: 3000,
        });
        this.showCauseForm = false;
        this.$emit('updated');
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.overview-commitment-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.overview-commitment-card__badge {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}

.overview-commitment-card__badge--open {
  background: #f2f2f2;
  color: #6b6b6b;
}

.overview-commitment-card__badge--overdue {
  background: #fee2e2;
  color: #991b1b;
}

.overview-commitment-card__badge--done {
  background: #dcfce7;
  color: #166534;
}

.overview-commitment-card__badge--not-done {
  background: #fee2e2;
  color: #991b1b;
}

.overview-commitment-card__avatar {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  background: var(--color-orange);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.overview-commitment-card__status-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-dark);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: normal;
  text-align: left;
  line-height: 1.3;
  max-width: 8.5rem;
}

.overview-commitment-card__status-trigger:hover {
  background: #f5f5f5;
}

.overview-commitment-card__dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 20;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 0.25rem 0;
}

.overview-commitment-card__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--color-dark);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
}

.overview-commitment-card__dropdown-item:hover {
  background: #f5f5f5;
}

.overview-commitment-card__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
}

.overview-commitment-card__cause {
  border-color: #e5e5e5;
}

.overview-commitment-card__btn-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.overview-commitment-card__btn-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: var(--color-dark);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.overview-commitment-card__btn-secondary:hover {
  background: #f5f5f5;
}
</style>
